// Buyback and trade-in record numbers: B-1001, B-1002, ...

export function buybackNumber(n: number): string {
  return `B-${n}`;
}

/** Reads "B-1001" or "b1001" back into 1001. */
export function parseBuybackNumber(q: string): number | null {
  const m = /^b[-\s]?0*(\d{1,7})$/i.exec(q.trim());
  return m ? Number(m[1]) : null;
}

// ---------------------------------------------------------------- the phone's label

/**
 * Label sizes for the sticker on the back of a bought phone. The Brother one (DK-1202, 62 x 100 mm)
 * and the 2-5/16 x 4 in DYMO 30256 fit the back of any iPhone and hold everything; the small
 * DYMO 30334 holds the essentials.
 */
export const BUY_LABEL_SIZES = {
  brother_62x100: 'Brother 2.4 × 3.9 in (DK-1202, 62 × 100 mm)',
  dymo_30256: '2.3 × 4 in (DYMO 30256, Zebra or Rollo 2.25 × 4)',
  dymo_30334: '2.25 × 1.25 in (DYMO 30334)',
} as const;
export type BuyLabelSize = keyof typeof BUY_LABEL_SIZES;
export const isBuyLabelSize = (v: unknown): v is BuyLabelSize => typeof v === 'string' && v in BUY_LABEL_SIZES;

// ---------------------------------------------------------------- paying the seller

/**
 * How the seller gets paid. The blue bag is the store's buyback money, kept apart from the
 * register; the cash drawer is for emergencies. Store credit is for trade-ins, and "other"
 * stays for records made before Zelle, Cash App and Apple Pay had their own buttons.
 */
export type Payout = 'blue_bag' | 'cash' | 'zelle' | 'cashapp' | 'apple_pay' | 'store_credit' | 'other';
export const PAYOUTS: Payout[] = ['blue_bag', 'cash', 'zelle', 'cashapp', 'apple_pay', 'store_credit', 'other'];
/** The buttons on the Buy a phone screen, in order. */
export const BUY_PAYOUTS: Payout[] = ['blue_bag', 'cash', 'zelle', 'cashapp', 'apple_pay'];

export const PAYOUT_LABELS: Record<string, string> = {
  blue_bag: 'Blue bag',
  cash: 'Cash drawer',
  zelle: 'Zelle',
  cashapp: 'Cash App',
  apple_pay: 'Apple Pay',
  store_credit: 'Store credit',
  other: 'Other',
};

/** Paid with an app (no cash changes hands). */
export const isAppPayout = (m: string) => m === 'zelle' || m === 'cashapp' || m === 'apple_pay';

/** "Blue bag", "Zelle · 3:42 PM", or the old free-text "Zelle" for "other". */
export function payoutLabel(method: string, ref?: string | null): string {
  if (method === 'other') return ref?.trim() || 'Other';
  const label = PAYOUT_LABELS[method] ?? method;
  return ref?.trim() ? `${label} · ${ref.trim()}` : label;
}

/** What the seller sees on their receipt: cash is cash, whichever bag it came from. */
export function sellerPayoutLabel(method: string, ref?: string | null): string {
  if (method === 'blue_bag' || method === 'cash') return 'Cash';
  return payoutLabel(method, ref);
}

// ---------------------------------------------------------------- a wholesale buyer paying the store

/**
 * How a wholesale buyer paid for the phones they took. Cash goes into the register's drawer (a
 * cash movement the drawer count expects); Zelle, card, check and other are recorded only.
 */
export type WholesalePay = 'cash' | 'zelle' | 'card' | 'check' | 'other';
export const WHOLESALE_PAYS: WholesalePay[] = ['cash', 'zelle', 'card', 'check', 'other'];
export const WHOLESALE_PAY_LABELS: Record<string, string> = { cash: 'Cash', zelle: 'Zelle', card: 'Card', check: 'Check', other: 'Other' };
export const isWholesalePay = (v: unknown): v is WholesalePay => typeof v === 'string' && (WHOLESALE_PAYS as string[]).includes(v);

/** "Cash", "Zelle · 8841", "Check · #1203", what "other" was ("Venmo"), or "Not recorded" for older wholesales. */
export function wholesalePaidLabel(method: string | null | undefined, ref?: string | null): string {
  if (!method) return 'Not recorded';
  if (method === 'other') return ref?.trim() || 'Other';
  const label = WHOLESALE_PAY_LABELS[method] ?? method;
  return ref?.trim() ? `${label} · ${ref.trim()}` : label;
}

// ---------------------------------------------------------------- IMEI checks (SICKW)

/** Names for the SICKW services a phone store uses most (shown above each result). */
export const SICKW_SERVICE_NAMES: Record<string, string> = {
  '1': 'Samsung Info Pro',
  '3': 'iCloud On/Off',
  '4': 'iCloud Clean/Lost',
  '6': 'Blacklist Status Pro',
  '13': 'Motorola Info',
  '16': 'T-Mobile USA Status',
  '21': 'Cricket USA Status',
  '42': 'Google Pixel Info',
  '54': 'Blacklist Status',
  '61': 'iPhone Carrier & FMI & Blacklist',
  '78': 'iPhone Carrier & FMI',
  '80': 'Samsung Info',
  '82': 'Samsung Knox Guard',
  '103': 'iPhone Carrier',
  '147': 'iPhone Carrier & FMI & Blacklist S2',
  '232': 'Verizon USA Status',
};

export const sickwServiceName = (id: string) => SICKW_SERVICE_NAMES[id] ?? `SICKW check #${id}`;

/** One SICKW service's answer, read into "label: value" lines. */
export interface ImeiCheckSection {
  service: string;
  title: string;
  ok: boolean;
  fields: [string, string][];
  error?: string | null;
}

/** "61" or "80, 54" → ["80", "54"]. */
export function parseServiceList(v: string | null | undefined): string[] {
  return [...new Set((v ?? '').split(/[\s,]+/).map((s) => s.trim()).filter((s) => /^\d{1,4}$/.test(s)))].slice(0, 3);
}
